import { createTheme, extendTheme } from '@mui/material/styles';

import { PALETTE } from './palette';
import {
  FONT_SIZE,
  getResponsiveTypography,
  HTML_FONT_SIZE,
} from './typography';

const GRADIENTS = {
  button: `linear-gradient(88deg, ${PALETTE.salmon} 0%, ${PALETTE.orange} 100%)`,
  primary: `linear-gradient(85deg, ${PALETTE.navy} 0%, ${PALETTE.teal200} 100%)`,
  primary2: 'linear-gradient(90deg, #2176C3 0%, #3AB4C1 100%)',
  banner: `linear-gradient(224.42deg, ${PALETTE.orange} 0%, ${PALETTE.salmon} 100%)`,
  forgotPassword: `linear-gradient(45deg, ${PALETTE.navy} 0%, ${PALETTE.teal200} 100%);`,
  premium: `linear-gradient(90deg, ${PALETTE.goldLight} 0%, ${PALETTE.goldDark} 100%)`,
  radial: `radial-gradient(28.58% 97.28% at 50% 0%, ${PALETTE.blue20} 0%, ${PALETTE.blue10} 100%)`,
  promoCode: `linear-gradient(85deg, #A067E3 0%, ${PALETTE.navy} 50%, ${PALETTE.night150} 100%)`,
} as const;

const defaultTheme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data',
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1500,
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          hero1: 'h1',
          hero2: 'h1',
          hero3: 'h1',
          hero4: 'h1',
          h7: 'h6',
          eyebrowHeader1: 'p',
          eyebrowHeader2: 'p',
          body1SemiBold: 'p',
          body2SemiBold: 'p',
          body3: 'p',
          body3SemiBold: 'p',
          button1: 'span',
          button2: 'span',
          caption1: 'span',
          caption1Bold: 'span',
          caption2: 'span',
          caption2Bold: 'span',
        },
      },
    },
  },
});

const { breakpoints } = defaultTheme;

const customTheme = {
  colorSchemes: {
    light: {
      palette: {
        ...PALETTE,
        primary: {
          main: PALETTE.night200,
          active: PALETTE.blueBlack,
          light: PALETTE.night50,
        },
        secondary: {
          main: PALETTE.purple200,
          active: PALETTE.purple150,
          border: PALETTE.purple100,
          medium: PALETTE.purple50,
          light: PALETTE.purple25,
        },
        accent: {
          main: PALETTE.teal200,
        },
        text: {
          main: PALETTE.grey200,
          light: PALETTE.grey150,
          trace: PALETTE.grey100,
          contrast: PALETTE.white,
        },
        surface: {
          background: PALETTE.white,
          base: PALETTE.grey50,
        },
        chapter: {
          main: PALETTE.grey50,
          active: PALETTE.purple50,
          completed: PALETTE.night50,
          quizReady: PALETTE.purple200,
          text: PALETTE.grey200,
          contrastText: PALETTE.white,
          activeText: PALETTE.grey200,
        },
        header: {
          icon: PALETTE.white,
          background: PALETTE.white,
        },
        sidebar: {
          background: PALETTE.grey50,
          hover: 'rgba(0, 0, 0, 0.1)',
          selected: PALETTE.night200,
          selectedText: PALETTE.white,
        },
        content: {
          green3: '#6EA88E',
          teal3: '#5DACBC',
          blue3: '#4986E8',
          purple3: '#AE83E1',
          red3: '#E35E6B',
          orange3: '#FF9C40',
          yellow3: '#FDC454',
          neutral3: '#BFB3B2',
        },
        gradients: {
          ...GRADIENTS,
          button: `linear-gradient(88deg, ${PALETTE.purple200} 0%, ${PALETTE.navy} 100%)`,
          primary: `linear-gradient(85deg, ${PALETTE.purple200} 0%, ${PALETTE.navy} 100%)`,
          primary2: `linear-gradient(90deg, ${PALETTE.purple200} 0%, ${PALETTE.purple150} 100%)`,
          secondary: `linear-gradient(74deg, ${PALETTE.purple50} 31.09%, ${PALETTE.purple100} 100%)`,
          banner: `linear-gradient(224.42deg, ${PALETTE.purple150} 0%, ${PALETTE.purple200} 100%)`,
          radial: `radial-gradient(28.58% 97.28% at 50% 0%, ${PALETTE.purple100} 0%, ${PALETTE.purple50} 100%)`,
          pricingBackground: `linear-gradient(61deg, ${PALETTE.white} 18.27%, ${PALETTE.purple50} 53.44%, ${PALETTE.white} 89.68%)`,
        },
        charts: {
          reports: {
            users: PALETTE.navy,
            started: PALETTE.teal50,
            earned: PALETTE.purple150,
            distribution: {
              main: PALETTE.purple150,
              hover: PALETTE.purple200,
            },
          },
        },
        members: {
          unassigned: PALETTE.red,
          invited: PALETTE.teal200,
          active: PALETTE.purple200,
          removed: PALETTE.grey100,
        },
      },
      typography: {
        fontSize: FONT_SIZE,
        htmlFontSize: HTML_FONT_SIZE,
        ...getResponsiveTypography(breakpoints.down('md')),
      },
    },
  },
  zIndex: {
    drawer: { xs: 1250, md: 1100 },
    overlay: 1000,
    appBar: 1200,
    player: 1300,
    tooltip: 1250,
    featureAnnouncement: 1200,
  },
  customShadows: {
    modalFooter: '0px -2px 4px rgba(48, 56, 75, 0.10)',
    header: '0px 4px 8px rgba(0, 0, 0, 0.4)',
    cardFlair: '0px 3px 5px rgba(0, 0, 0, 0.25)',
    button:
      '0px 3px 1px -2px rgba(0, 0, 0, 0.20), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
    newFeatureBanner: '0 4px 10px 0 rgba(0, 0, 0, 0.10)',
  },
  dimensions: {
    header: {
      height: 64,
    },
    navBanner: {
      height: 40,
    },
    sidebar: {
      width: 255, // TODO delete this eventually
    },
    pageSidebar: {
      width: 300,
    },
    // why does AssessmentModalContent and EpisodeCompleted use this?
    episodeCardCover: {
      width: 190,
      height: 190,
    },
    miniPlayer: {
      height: {
        xs: 66,
        sm: 90,
      },
    },
    appLayout: {
      px: { xs: 2, lg: 6 },
      py: { xs: 2, md: 4 },
      maxWidth: 1440,
    },
  },
};

export const theme = extendTheme(defaultTheme, customTheme);

export type CustomTheme = typeof customTheme;

export type Palette = CustomTheme['colorSchemes']['light']['palette'];
